
import React, { useState } from 'react';
import { Lock, ArrowRight, Loader2, Info, UserPlus, Mail, User, CheckCircle, Store, KeyRound, X } from 'lucide-react';
import { User as UserType, SiteConfig } from '../types';

interface UnifiedLoginProps {
  onLogin: (username: string, password: string) => Promise<void>;
  onRegister: (newUser: Partial<UserType> & { password: string }) => Promise<void>;
  showToast: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
  siteConfig?: SiteConfig;
}

export const UnifiedLogin: React.FC<UnifiedLoginProps> = ({ onLogin, onRegister, showToast, siteConfig }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Login State
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Registration State
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regUsername, setRegUsername] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');

  // Forgot Password State
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password) {
      showToast('error', 'Authentication Failed', 'Please enter both username and password.');
      return;
    }
    
    setIsLoading(true);

    try {
      await onLogin(username, password);
    } catch (error: any) {
      console.error(error);
      showToast('error', 'Access Denied', error?.message || 'Invalid credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!regName || !regEmail || !regUsername || !regPassword || !regConfirmPassword) {
          showToast('error', 'Missing Fields', 'Please fill in all fields to create an account.');
          return;
      }

      if (regPassword !== regConfirmPassword) {
          showToast('error', 'Password Mismatch', 'Passwords do not match. Please try again.');
          return;
      }

      setIsLoading(true);

      try {
        await onRegister({
          name: regName,
          email: regEmail,
          username: regUsername,
          password: regPassword
        });
        setRegistrationSuccess(true);
        
        setRegName('');
        setRegEmail('');
        setRegUsername('');
        setRegPassword('');
        setRegConfirmPassword('');
      } catch (error: any) {
        console.error(error);
        showToast('error', 'Registration Failed', error?.message || 'Please try again or contact an administrator.');
      } finally {
        setIsLoading(false);
      }
  };

  const handleForgotSubmit = (e: React.FormEvent) => {
      e.preventDefault();
      if(!forgotEmail) {
          showToast('error', 'Required', 'Please enter your email or username.');
          return;
      }
      
      // Simulate API call for password reset
      setTimeout(() => {
          showToast('success', 'Request Sent', 'If an account exists, password reset instructions have been sent.');
          setShowForgotModal(false);
          setForgotEmail('');
      }, 1000);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-zinc-900 font-sans selection:bg-teal-500 selection:text-white">
      
      {/* Dynamic Background Elements */}
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1557683316-973673baf926?q=80&w=2029&auto=format&fit=crop')] bg-cover bg-center opacity-20 mix-blend-overlay" />
      <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-teal-500/30 rounded-full blur-[120px] mix-blend-screen animate-pulse" />
      <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-purple-500/20 rounded-full blur-[120px] mix-blend-screen animate-pulse animation-delay-2000" />
      
      {/* Main Glass Card */}
      <div className="relative z-10 w-full max-w-5xl h-auto min-h-[600px] bg-white/10 backdrop-blur-2xl rounded-3xl border border-white/20 shadow-2xl overflow-hidden flex flex-col md:flex-row m-4 animate-fade-in-up">
        
        {/* Left Side - Brand & Info */}
        <div className="w-full md:w-[45%] bg-gradient-to-br from-teal-900/90 to-zinc-900/90 p-10 flex flex-col justify-between text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            
            <div className="relative z-10">
                <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center backdrop-blur-md border border-white/10 mb-6 shadow-xl">
                    {siteConfig?.logo ? (
                        <img src={siteConfig.logo} alt="Logo" className="w-full h-full object-cover rounded-xl" />
                    ) : (
                        <Store className="w-8 h-8 text-white" />
                    )}
                </div>
                <h1 className="text-4xl font-extrabold tracking-tight leading-none mb-2">{siteConfig?.appName || 'STERLING'}</h1>
                <p className="text-teal-200/80 font-medium text-lg tracking-wide">{siteConfig?.appSubTitle || 'Discount Manager'}</p>
            </div>

            <div className="relative z-10 mt-12 md:mt-0">
                <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-teal-200 mb-3 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4" /> System Access
                    </h3>
                    <p className="text-sm text-zinc-300 leading-relaxed">
                        {siteConfig?.loginMessage || "Authorized personnel only. Manage campaigns, submit discounts, and track approval workflows in real-time."}
                    </p>
                </div>
            </div>
        </div>

        {/* Right Side - Form Container */}
        <div className="w-full md:w-[55%] bg-white p-8 md:p-12 flex flex-col justify-center relative">
            <div className="max-w-md mx-auto w-full">
                
                {!registrationSuccess && (
                     <div className="flex justify-between items-center mb-8">
                        <div>
                            <h2 className="text-2xl font-bold text-zinc-900 tracking-tight">
                                {isRegistering ? 'Create Account' : 'Welcome Back'}
                            </h2>
                            <p className="text-zinc-500 text-sm font-medium mt-1">
                                {isRegistering ? 'Join the management portal' : 'Please sign in to continue'}
                            </p>
                        </div>
                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${isRegistering ? 'bg-teal-50 text-teal-600' : 'bg-zinc-100 text-zinc-900'}`}>
                             {isRegistering ? <UserPlus className="w-6 h-6" /> : <KeyRound className="w-6 h-6" />}
                        </div>
                    </div>
                )}

                {registrationSuccess ? (
                    <div className="text-center animate-fade-in py-8">
                        <div className="w-24 h-24 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-teal-100">
                            <CheckCircle className="w-12 h-12 text-teal-600" />
                        </div>
                        <h2 className="text-2xl font-bold text-zinc-900 mb-4">Registration Successful!</h2>
                        <div className="bg-zinc-50 rounded-xl p-6 border border-zinc-100 mb-8">
                             <p className="text-zinc-600 leading-relaxed text-sm">
                                Your account has been created and is currently <span className="font-bold text-zinc-900">pending approval</span>.
                                <br/><br/>
                                Please contact the authority/administrator to activate your account.
                             </p>
                        </div>
                        <button 
                            onClick={() => {
                                setRegistrationSuccess(false);
                                setIsRegistering(false);
                            }}
                            className="w-full py-3 bg-zinc-900 text-white font-bold rounded-xl text-sm hover:bg-zinc-800 transition-colors shadow-lg"
                        >
                            Back to Login
                        </button>
                    </div>
                ) : isRegistering ? (
                    // REGISTRATION FORM
                    <form onSubmit={handleRegisterSubmit} className="space-y-4 animate-fade-in">
                        <div className="space-y-4">
                            <div className="relative group">
                                <User className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-teal-600 transition-colors" />
                                <input 
                                    type="text" 
                                    value={regName}
                                    onChange={(e) => setRegName(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-teal-100 focus:bg-white focus:border-teal-200" 
                                    placeholder="Full Name"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="relative group">
                                <Mail className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-teal-600 transition-colors" />
                                <input 
                                    type="email" 
                                    value={regEmail}
                                    onChange={(e) => setRegEmail(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-teal-100 focus:bg-white focus:border-teal-200" 
                                    placeholder="Email Address"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="relative group">
                                <User className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-teal-600 transition-colors" />
                                <input 
                                    type="text" 
                                    value={regUsername}
                                    onChange={(e) => setRegUsername(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-teal-100 focus:bg-white focus:border-teal-200" 
                                    placeholder="Username"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-teal-600 transition-colors" />
                                <input 
                                    type="password" 
                                    value={regPassword}
                                    onChange={(e) => setRegPassword(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-teal-100 focus:bg-white focus:border-teal-200" 
                                    placeholder="Password"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-teal-600 transition-colors" />
                                <input 
                                    type="password" 
                                    value={regConfirmPassword}
                                    onChange={(e) => setRegConfirmPassword(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-teal-100 focus:bg-white focus:border-teal-200" 
                                    placeholder="Confirm Password"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-xl text-sm px-5 py-4 mt-6 focus:outline-none shadow-lg transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed bg-teal-600 hover:bg-teal-700 shadow-teal-200"
                        >
                            {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Account'}
                        </button>

                        <div className="text-center pt-2">
                             <button 
                                type="button"
                                onClick={() => setIsRegistering(false)}
                                disabled={isLoading}
                                className="text-sm font-bold text-zinc-400 hover:text-zinc-600 transition-colors"
                            >
                                Back to Login
                            </button>
                        </div>
                    </form>
                ) : (
                    // LOGIN FORM
                    <form onSubmit={handleLogin} className="space-y-6 animate-fade-in">
                        <div className="space-y-4">
                            <div className="relative group">
                                <User className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-zinc-900 transition-colors" />
                                <input 
                                    type="text" 
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-zinc-200 focus:bg-white focus:border-zinc-400" 
                                    placeholder="Username"
                                    disabled={isLoading}
                                />
                            </div>
                            <div className="relative group">
                                <Lock className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400 group-focus-within:text-zinc-900 transition-colors" />
                                <input 
                                    type="password" 
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none transition-all font-semibold focus:ring-2 focus:ring-zinc-200 focus:bg-white focus:border-zinc-400" 
                                    placeholder="Password"
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between text-xs font-bold text-zinc-400">
                             <label className="flex items-center gap-2 cursor-pointer hover:text-zinc-600">
                                 <input type="checkbox" className="rounded border-zinc-300 text-teal-600 focus:ring-teal-500" />
                                 Remember me
                             </label>
                             <button type="button" onClick={() => setShowForgotModal(true)} className="hover:text-teal-600 transition-colors">Forgot Password?</button>
                        </div>

                        <button 
                            type="submit" 
                            disabled={isLoading}
                            className="w-full flex items-center justify-center gap-2 text-white font-bold rounded-xl text-base px-5 py-4 focus:outline-none shadow-xl transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed bg-zinc-900 hover:bg-zinc-800 shadow-zinc-200"
                        >
                            {isLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <>
                                    Sign In <ArrowRight className="w-5 h-5" />
                                </>
                            )}
                        </button>

                        <div className="relative py-2">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-zinc-100"></div>
                            </div>
                            <div className="relative flex justify-center text-xs">
                                <span className="px-2 bg-white text-zinc-400 font-medium">New to the portal?</span>
                            </div>
                        </div>

                        <button 
                            type="button"
                            onClick={() => setIsRegistering(true)}
                            disabled={isLoading}
                            className="w-full py-3 bg-teal-50 text-teal-700 font-bold rounded-xl text-sm hover:bg-teal-100 transition-colors border border-teal-100"
                        >
                            Create an Account
                        </button>
                    </form>
                )}
            </div>
        </div>
      </div>
      
      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 relative">
                <button onClick={() => setShowForgotModal(false)} className="absolute top-4 right-4 text-zinc-400 hover:text-zinc-600">
                    <X className="w-5 h-5" />
                </button>
                <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mb-4 mx-auto text-teal-600">
                    <KeyRound className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900 text-center mb-2">Reset Password</h3>
                <p className="text-sm text-zinc-500 text-center mb-6">Enter your username or email address and we'll send you a link to reset your password.</p>
                
                <form onSubmit={handleForgotSubmit}>
                    <div className="relative group mb-6">
                        <Mail className="absolute left-4 top-3.5 w-5 h-5 text-zinc-400" />
                        <input 
                            type="text" 
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            className="w-full bg-zinc-50 border border-zinc-200 text-zinc-900 text-sm rounded-xl block p-3.5 pl-12 outline-none font-semibold focus:ring-2 focus:ring-teal-100" 
                            placeholder="Email or Username"
                        />
                    </div>
                    <button 
                        type="submit" 
                        className="w-full py-3 bg-zinc-900 text-white font-bold rounded-xl shadow-lg hover:bg-zinc-800 transition-all active:scale-95"
                    >
                        Send Request
                    </button>
                </form>
            </div>
        </div>
      )}

      {/* Footer Info */}
      <div className="absolute bottom-6 left-0 right-0 text-center z-10">
          <p className="text-zinc-500/50 text-xs font-bold">© 2025 Sterling Aggregator Manager. Secure System.</p>
      </div>
    </div>
  );
};
